import { useState, useEffect } from 'react';
import { apiFetch } from '../../lib/api';
import { Users, Activity, Package, Layers, RefreshCw } from 'lucide-react';

interface AdminUser {
  id: string;
  email: string;
  display_name: string | null;
  role: string;
  created_at: string;
  last_login: string | null;
}

interface AdminStats {
  totalUsers: number;
  activeSessions: number;
  totalPackages: number;
  totalScenarios: number;
}

export function AdminPanel() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, statsRes] = await Promise.all([
        apiFetch('/api/admin/users'),
        apiFetch('/api/admin/stats'),
      ]);
      if (usersRes.ok) setUsers(await usersRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const statCards = stats ? [
    { label: 'Total Users', value: stats.totalUsers, icon: Users, color: 'text-chart-teal' },
    { label: 'Active Sessions', value: stats.activeSessions, icon: Activity, color: 'text-chart-purple' },
    { label: 'Saved Packages', value: stats.totalPackages, icon: Package, color: 'text-chart-blue' },
    { label: 'Saved Scenarios', value: stats.totalScenarios, icon: Layers, color: 'text-chart-amber' },
  ] : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-display font-bold text-primary">Admin Panel</h2>
          <p className="text-sm text-secondary mt-1">System overview and user management</p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-border text-secondary hover:text-primary hover:bg-surface transition-all disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {statCards.map(card => {
            const Icon = card.icon;
            return (
              <div key={card.label} className="card">
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={16} className={card.color} />
                  <span className="text-xs text-muted uppercase tracking-wider font-medium">{card.label}</span>
                </div>
                <div className="text-2xl font-bold text-primary">{card.value}</div>
              </div>
            );
          })}
        </div>
      )}

      <div className="card">
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider mb-4">Registered Users</h3>
        {loading ? (
          <div className="text-sm text-muted py-8 text-center">Loading...</div>
        ) : users.length === 0 ? (
          <div className="text-sm text-muted py-8 text-center">No users found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3 text-xs text-muted uppercase tracking-wider font-medium">Email</th>
                  <th className="text-left py-2 px-3 text-xs text-muted uppercase tracking-wider font-medium">Name</th>
                  <th className="text-left py-2 px-3 text-xs text-muted uppercase tracking-wider font-medium">Role</th>
                  <th className="text-left py-2 px-3 text-xs text-muted uppercase tracking-wider font-medium">Joined</th>
                  <th className="text-left py-2 px-3 text-xs text-muted uppercase tracking-wider font-medium">Last Login</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} className="border-b border-border/50 hover:bg-surface/50 transition-colors">
                    <td className="py-2.5 px-3 text-primary font-medium">{user.email}</td>
                    <td className="py-2.5 px-3 text-secondary">{user.display_name || '—'}</td>
                    <td className="py-2.5 px-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-micro font-medium ${
                        user.role === 'super_admin'
                          ? 'bg-chart-purple/15 text-chart-purple'
                          : 'bg-chart-teal/15 text-chart-teal'
                      }`}>
                        {user.role === 'super_admin' ? 'Super Admin' : 'User'}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-muted text-xs">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-2.5 px-3 text-muted text-xs">
                      {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
